import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle, Check, CheckCheck, Copy } from "lucide-react";
import { toast } from "sonner";
import type { ChatMessage, Booking } from "@/types";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminChat = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef(null);
  const intervalRef = useRef(null);

  const customerChatLink = booking
    ? `${window.location.origin}/chat/${bookingId}?token=${booking.chat_token}`
    : "";

  const hasLineRelay = !!booking?.customer_line_user_id;

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/${bookingId}/messages?token=${token}`);
      setMessages(res.data.messages);
      setError(null);
      if (!booking) {
        const bookingRes = await axios.get(`${API}/chat/${bookingId}/booking-info?token=${token}`);
        setBooking(bookingRes.data);
      }
      await axios.post(`${API}/chat/${bookingId}/mark-read?token=${token}`, { reader_type: "therapist" }).catch(() => {});
    } catch {
      if (!messages.length) setError("Unable to load chat. The link may be invalid or expired.");
    }
  }, [bookingId, token, booking, messages.length]);

  useEffect(() => {
    if (bookingId && token) {
      fetchMessages();
      intervalRef.current = setInterval(fetchMessages, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [bookingId, token, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/chat/${bookingId}/therapist-send`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage("");
      if (res.data.line_sent) {
        toast.success("Delivered via LINE");
      }
      fetchMessages();
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const copyCustomerLink = () => {
    navigator.clipboard.writeText(customerChatLink).then(() => toast.success("Customer chat link copied!"));
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
          <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="admin-chat-page">
      <header className="bg-white shadow-sm border-b px-4 py-3">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_spa-bookings-5/artifacts/syk2guux_Angel-Massage-logo-png.png"
              alt="Angel Massage"
              className="h-8 w-auto"
            />
            <div className="flex-1">
              <h1 className="text-sm font-semibold text-gray-800">Angel Massage - Admin Chat</h1>
              <p className="text-xs text-gray-500">
                {booking ? `${booking.name} | ${booking.hotel_name}, Room ${booking.room_number}` : "Loading..."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs">
            {hasLineRelay ? (
              <span className="text-green-600 font-medium" data-testid="line-relay-active">Messages auto-relay to customer via LINE</span>
            ) : (
              <span className="text-orange-500 font-medium" data-testid="line-relay-inactive">LINE relay not available — share chat link with customer</span>
            )}
            <button
              onClick={copyCustomerLink}
              className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700 underline ml-auto"
              data-testid="copy-customer-link"
            >
              <Copy className="w-3 h-3" /> Copy customer link
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 max-w-lg mx-auto w-full">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 text-sm mt-8">
            <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No messages yet. Send a message to the customer.</p>
            {hasLineRelay && <p className="text-xs mt-1 text-green-600">Messages will be delivered via LINE</p>}
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex mb-3 ${msg.sender_type === "therapist" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                msg.sender_type === "therapist"
                  ? "bg-pink-500 text-white rounded-br-sm"
                  : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm"
              }`}
            >
              <p className="text-xs font-medium opacity-70 mb-1">
                {msg.sender_type === "therapist" ? "You (Admin)" : msg.sender_name}
                {msg.sent_via === "line" ? " (LINE)" : ""}
                {msg.original_lang ? ` [${msg.original_lang.toUpperCase()}]` : ""}
              </p>
              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
              {msg.translated_message && (
                <div className={`mt-1.5 pt-1.5 text-sm whitespace-pre-wrap italic ${
                  msg.sender_type === "therapist" ? "border-t border-white/20 text-white/80" : "border-t border-gray-200 text-gray-500"
                }`}>
                  <span className="text-[10px] font-semibold uppercase not-italic">{msg.sender_type === "customer" ? "EN" : msg.original_lang === "en" ? "TH" : "EN"}:</span> {msg.translated_message}
                </div>
              )}
              {msg.sender_type === "therapist" && (
                <div className="flex justify-end mt-1">
                  {msg.read_at ? (
                    <CheckCheck className="w-3.5 h-3.5 text-blue-200" />
                  ) : (
                    <Check className="w-3.5 h-3.5 text-white/50" />
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </main>

      <footer className="bg-white border-t px-4 py-3">
        <div className="max-w-lg mx-auto flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder="Type a message to customer..."
            className="flex-1"
            data-testid="admin-chat-input"
          />
          <Button onClick={handleSend} disabled={sending || !newMessage.trim()} className="bg-pink-500 hover:bg-pink-600" data-testid="admin-chat-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default AdminChat;
